type DamCardDateKind = 'edited' | 'uploaded';

type Translate = (key: string, interpolationValues?: Record<string, string | number>) => string;

interface DamCardDate {
    kind: DamCardDateKind;
    date: Date;
}

const MESSAGE_KEYS: Record<DamCardDateKind, { today: string; yesterday: string; daysAgo: string; on: string }> = {
    edited: {
        today: 'DAM.CARD.EDITED_TODAY',
        yesterday: 'DAM.CARD.EDITED_YESTERDAY',
        daysAgo: 'DAM.CARD.EDITED_DAYS_AGO',
        on: 'DAM.CARD.EDITED_ON',
    },
    uploaded: {
        today: 'DAM.CARD.UPLOADED_TODAY',
        yesterday: 'DAM.CARD.UPLOADED_YESTERDAY',
        daysAgo: 'DAM.CARD.UPLOADED_DAYS_AGO',
        on: 'DAM.CARD.UPLOADED_ON',
    },
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toDate = (value: unknown): Date | undefined => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    const date = value instanceof Date ? value : new Date(value as string | number);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

/**
 * Picks the one date the card shows: the edit date once the asset changed after
 * the upload (`updatedAt__` later than `createdAt__`), otherwise the upload date.
 */
const resolveDamCardDate = (createdAt: unknown, updatedAt: unknown): DamCardDate | undefined => {
    const created = toDate(createdAt);
    const updated = toDate(updatedAt);

    if (created && updated && updated.getTime() > created.getTime()) {
        return { kind: 'edited', date: updated };
    }
    if (created) {
        return { kind: 'uploaded', date: created };
    }
    return updated ? { kind: 'edited', date: updated } : undefined;
};

const startOfDay = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** Calendar days between `date` and `now` in local time; 0 = today. */
const daysBetween = (date: Date, now: Date): number => Math.round((startOfDay(now) - startOfDay(date)) / MS_PER_DAY);

/** English dates use DD/MM/YYYY (en-GB), other languages their own short date. */
const dateLocale = (language: string): string => (language.toLowerCase().startsWith('en') ? 'en-GB' : language.replace('_', '-'));

/**
 * Card date text per the handoff date rules: today with time, yesterday,
 * 2–6 days ago, from 7 days on as date.
 */
const formatDamCardDate = (
    t: Translate,
    { kind, date }: DamCardDate,
    language: string,
    now: Date = new Date(),
): string => {

    const keys = MESSAGE_KEYS[kind];
    const days = daysBetween(date, now);
    const locale = dateLocale(language);

    if (days <= 0) {
        const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
        return t(keys.today, { time });
    }
    if (days === 1) {
        return t(keys.yesterday);
    }
    if (days < 7) {
        return t(keys.daysAgo, { count: days });
    }
    const formatted = date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
    return t(keys.on, { date: formatted });
};

export { formatDamCardDate, resolveDamCardDate };
export type { DamCardDate, DamCardDateKind };
